import { useState } from "react";
import { Linkedin, Twitter, Youtube, Facebook, Instagram, ArrowUpRight, Send, ArrowRight } from "lucide-react";

const COLUMNS = [
  {
    title: "Useful Links",
    links: ["About Us", "Case Studies", "Careers", "Partners", "Contact"],
  },
  {
    title: "Services",
    links: ["Installation", "AMC & Support", "Training", "Data Migration", "Customization"],
  },
  {
    title: "Product",
    links: ["TallyPrime Silver", "TallyPrime Gold", "TallyPrime Rental", "Enterprise", "Add-ons & TDL"],
  },
];

const SOCIALS = [
  { Icon: Linkedin, href: "#", label: "LinkedIn" },
  { Icon: Twitter, href: "#", label: "Twitter" },
  { Icon: Youtube, href: "#", label: "YouTube" },
  { Icon: Facebook, href: "#", label: "Facebook" },
  { Icon: Instagram, href: "#", label: "Instagram" },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  return (
    <footer data-testid="site-footer" className="relative bg-[#072A66] text-white pt-20 pb-10 overflow-hidden circuit-pattern">
      <div className="orb bg-[#FFA000] w-[400px] h-[400px] -top-40 left-1/2 -translate-x-1/2 opacity-10" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* CTA row */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0D47A1] via-[#0A3680] to-[#072A66] border border-white/10 p-8 lg:p-12 mb-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tight text-white max-w-xl">
              Ready to power your business with TallyPrime?
            </h3>
            <p className="mt-3 text-white/70 text-[15px] max-w-lg">
              Book a free 30-minute consultation with a certified TallyPrime expert.
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="footer-cta"
            className="btn-magnetic inline-flex items-center gap-2 rounded-lg bg-[#FFA000] hover:bg-[#E69000] text-white px-7 h-14 font-bold self-start shadow-[0_15px_40px_rgba(255,160,0,0.3)]"
          >
            Book a Free Demo
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 lg:gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-white px-3 py-1.5 shadow-[0_4px_20px_rgba(255,255,255,0.1)]">
                <img src="/logo.png" alt="Prime Technology" className="h-8 object-contain" />
              </div>
              <div className="h-10 w-px bg-white/20" />
              <img src="/certificate.jpg" alt="Tally Certified Partner" className="h-8 object-contain opacity-90" />
            </div>
            <p className="mt-5 text-[14px] text-white/60 leading-relaxed max-w-xs">
              Authorized TallyPrime distributor and implementation partner — helping 1500+ Indian businesses
              accelerate finance, GST and compliance.
            </p>

            {/* Newsletter */}
            <div className="mt-6">
              <h4 className="text-[12px] font-bold tracking-[0.14em] uppercase text-white/50 mb-3">Newsletter</h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 bg-white/5 border border-white/15 rounded-lg h-11 px-4 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FFA000]/50"
                />
                <button className="w-11 h-11 rounded-lg bg-[#FFA000] hover:bg-[#E69000] flex items-center justify-center transition">
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="w-10 h-10 rounded-full border border-white/15 hover:border-[#FFA000] hover:bg-[#FFA000]/15 flex items-center justify-center transition"
                >
                  <Icon className="w-4 h-4 text-white/80" />
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="text-[12px] font-bold tracking-[0.14em] uppercase text-white/50">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l}>
                    <a
                      href="#contact"
                      onClick={(e) => {
                        e.preventDefault();
                        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="text-[14px] text-white/80 hover:text-[#FFA000] transition-colors"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact info */}
          <div>
            <h4 className="text-[12px] font-bold tracking-[0.14em] uppercase text-white/50">Contact</h4>
            <ul className="mt-5 space-y-3 text-[14px] text-white/80">
              <li>Lucknow, Uttar Pradesh</li>
              <li>
                <a href="tel:+918090773315" className="hover:text-[#FFA000] transition-colors">+91 80907 73315</a>
              </li>
              <li>
                <a href="mailto:Support@primetechnology.com" className="hover:text-[#FFA000] transition-colors">Support@primetechnology.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[13px] text-white/50">© {new Date().getFullYear()} TallyPrime Solutions. All rights reserved.</p>
          <div className="flex items-center gap-6 text-[13px] text-white/50">
            <a href="#" className="hover:text-white transition">Privacy</a>
            <a href="#" className="hover:text-white transition">Terms</a>
            <a href="#" className="hover:text-white transition">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
